import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { 
  MessageCircle, 
  Send, 
  Bot, 
  User, 
  Plus, 
  Trash2, 
  Settings, 
  DollarSign, 
  Zap,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  CreditCard,
  X,
  Calendar,
  Clock,
  Copy,
  Check,
  MoreVertical,
  Mail,
  MessageSquare,
  Share2,
  Image,
  Download,
  Paperclip,
  FileText
} from 'lucide-react';

const AIChatbot = () => {
  const { user, API_BASE_URL } = useAuth();
  const { toast } = useToast();

  // Format timestamp in user-friendly format like "Oct 2nd, 9:10 AM"
  const formatFriendlyTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd'; 
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const month = months[date.getMonth()];
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    // Show "Today" for today's messages, otherwise show date
    if (messageDate.getTime() === today.getTime()) {
      return `Today, ${time}`;
    } else {
      return `${month} ${day}${ordinalSuffix}, ${time}`;
    }
  };
  const messagesEndRef = useRef(null);
  
  // Social Media Platform Configuration
  const socialMediaPlatforms = {
    instagram: {
      name: 'Instagram',
      icon: '📷',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        story_reels: 'Story/Reels',
        cover_photo: 'Cover Photo'
      }
    },
    facebook: {
      name: 'Facebook',
      icon: '📘',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        story_reels: 'Stories and Reels',
        cover_photo: 'Cover Photo'
      }
    },
    tiktok: {
      name: 'TikTok',
      icon: '🎵',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        story_reels: 'Stories'
      }
    },
    x: {
      name: 'X (Twitter)',
      icon: '🐦',
      formats: {
        post: 'Post',
        profile_picture: 'Profile Picture',
        landscape: 'Landscape',
        square: 'Square',
        cover_photo: 'Cover Photo'
      }
    },
    whatsapp: {
      name: 'WhatsApp',
      icon: '💬',
      formats: {
        post: 'Post'
      }
    },
    marketing: {
      name: 'Promotion Material',
      icon: '🎨',
      formats: {
        poster: 'Poster',
        flyer: 'Flyer',
        square: 'Square'
      }
    }
  };
  
  // State management
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [textModels, setTextModels] = useState([]); // Separate state for text models
  const [imageModels, setImageModels] = useState([]); // Separate state for image models
  const [aiSettings, setAiSettings] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [showUsageStats, setShowUsageStats] = useState(false);
  
  // Model selection state - defaults to settings, can be overridden by user
  const [selectedTextModel, setSelectedTextModel] = useState(null);
  const [selectedImageModel, setSelectedImageModel] = useState(null);
  
  // Modal state
  const [showChatModal, setShowChatModal] = useState(false);
  const [modalSession, setModalSession] = useState(null);
  const [showDeletingModal, setShowDeletingModal] = useState(false); // Deleting modal state
  
  // Input textarea state
  const [textareaRows, setTextareaRows] = useState(2);
  
  // Format menu state
  const [showFormatMenu, setShowFormatMenu] = useState(null); // messageId when menu is open
  const [isGeneratingImage, setIsGeneratingImage] = useState(null); // messageId when generating image
  const [showGeneratingModal, setShowGeneratingModal] = useState(false); // Show generating modal
  const [generatingMessage, setGeneratingMessage] = useState("Optimizing content & generating image..."); // Dynamic message
  const [errorModal, setErrorModal] = useState({ show: false, message: '' }); // Error modal state
  
  // Social Media Image Generation state
  const [showImageGenMenu, setShowImageGenMenu] = useState(null); // messageId when dropdown is open
  // Platform/format selection stored in database per session
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [showPlatformMenu, setShowPlatformMenu] = useState(null);
  const [showFormatSubMenu, setShowFormatSubMenu] = useState(null);
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  
  // Copy modal state
  const [showCopyModal, setShowCopyModal] = useState(false);

  // Auto-scroll to bottom of messages
  const scrollToBottom = (withOffset = false) => {
    if (withOffset) {
      // For format actions, scroll to show loading animation with proper spacing
      messagesEndRef.current?.scrollIntoView({ 
        behavior: "smooth", 
        block: "center",  // Center the element in viewport instead of bottom
        inline: "nearest" 
      });
      
      // Add a small additional offset to ensure full visibility with space below
      setTimeout(() => {
        const container = messagesEndRef.current?.parentElement?.parentElement;
        if (container) {
          container.scrollTop += 85; // Increased from 60px to 85px (additional 25px as requested)
        }
      }, 100);
    } else {
      // Regular scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize data on component mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Save platform/format selections to database when they change
  useEffect(() => {
    if (currentSession && (selectedPlatform || selectedFormat)) {
      updateSessionPlatform(selectedPlatform, selectedFormat);
    }
  }, [selectedPlatform, selectedFormat, currentSession]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadSessions(),
        loadAvailableModels(), 
        loadAiSettings(),
        loadUsageStats()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: "Error",
        description: "Failed to load AI Chatbot data. Please refresh the page.",
        variant: "destructive"
      });
    }
  };

  const loadSessions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/sessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
        
        // Don't auto-create or auto-load sessions - let user choose
      } else {
        throw new Error('Failed to load sessions');
      }
    } catch (error) {
      console.error('🔴 AI CHATBOT FRONTEND ERROR: Error loading sessions:', error);
      console.error('🔴 AI CHATBOT FRONTEND ERROR: Error message:', error.message);
      console.error('🔴 AI CHATBOT FRONTEND ERROR: Error stack:', error.stack);
    }
  };

  const loadAvailableModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/models`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableModels(Object.entries(data.models || {}));
      }
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const loadAiSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAiSettings(data.settings);
      }
      
      // Also load OpenAI settings to check if override is enabled
      const openaiResponse = await fetch(`${API_BASE_URL}/api/openai-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (openaiResponse.ok) {
        const openaiData = await openaiResponse.json();
        if (openaiData.success && openaiData.settings) {
          const useOpenAI = openaiData.settings.use_openai_for_all_services;
          
          console.log('🔧 OpenAI override enabled:', useOpenAI);
          
          // If OpenAI override is enabled, use OpenAI models
          if (useOpenAI) {
            const textModelsData = openaiData.text_models || [];
            const imageModelsData = openaiData.image_models || [];
            
            console.log('📝 Text models loaded:', textModelsData);
            console.log('🖼️ Image models loaded:', imageModelsData);
            
            // Set separate arrays for text and image models
            setTextModels(textModelsData);
            setImageModels(imageModelsData);
            
            // Set default models from OpenAI settings
            const defaultTextModel = openaiData.settings.text_generation_model || 'gpt-4o';
            const defaultImageModel = openaiData.settings.image_generation_model || 'dall-e-3';
            
            console.log('🎯 Default text model:', defaultTextModel);
            console.log('🎯 Default image model:', defaultImageModel);
            
            if (!selectedTextModel) {
              setSelectedTextModel(defaultTextModel);
            }
            if (!selectedImageModel) {
              setSelectedImageModel(defaultImageModel);
            }
          } else {
            // Use OpenRouter models - need to filter by category
            // For now, use all models for both (backend will handle)
            const allModels = Object.entries(data.models || {});
            
            // Filter models by category if available
            const textOnlyModels = allModels.filter(([id, info]) => {
              const category = typeof info === 'object' ? info.category : null;
              return !category || category === 'text' || !id.includes('dall-e') && !id.includes('image') && !id.includes('gemini');
            });
            
            const imageOnlyModels = allModels.filter(([id, info]) => {
              const category = typeof info === 'object' ? info.category : null;
              return category === 'image' || id.includes('dall-e') || id.includes('image') || id.includes('gemini');
            });
            
            setTextModels(textOnlyModels.map(([id, info]) => ({
              value: id,
              label: typeof info === 'string' ? info : info.name || id
            })));
            
            setImageModels(imageOnlyModels.map(([id, info]) => ({
              value: id,
              label: typeof info === 'string' ? info : info.name || id
            })));
            
            // Set default models from OpenRouter settings
            if (!selectedTextModel && data.settings?.openrouter_default_model) {
              setSelectedTextModel(data.settings.openrouter_default_model);
            }
            if (!selectedImageModel && data.settings?.openrouter_image_model) {
              setSelectedImageModel(data.settings.openrouter_image_model);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
    }
  };

  const loadUsageStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/usage-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const updateSessionPlatform = async (platform, format) => {
    if (!currentSession) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/sessions/${currentSession.id}/platform`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform: platform,
          format: format
        })
      });

      if (!response.ok) {
        console.error('Failed to update session platform/format');
      }
    } catch (error) {
      console.error('Error updating session platform/format:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });


      if (response.ok) {
        const data = await response.json();
        const newSession = data.session;
        setSessions(prev => [newSession, ...prev]);
        
        // Open the new session in modal
        openChatModal(newSession);
        
      } else {
        throw new Error('Failed to create session');
      }
    } catch (error) {
      console.error('🔴 AI CHATBOT FRONTEND ERROR: Error creating session:', error);
      console.error('🔴 AI CHATBOT FRONTEND ERROR: Error message:', error.message);
      console.error('🔴 AI CHATBOT FRONTEND ERROR: Error stack:', error.stack);
      toast({
        title: "Error",
        description: "Failed to create new chat session",
        variant: "destructive"
      });
    }
  };

  const loadSessionMessages = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/sessions/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Process messages to handle AI-generated images and filter out formatting prompts
        const processedMessages = data.messages
          .filter(message => {
            // Filter out formatting prompts (messages that contain platform-specific instructions)
            const content = message.content || '';
            const isFormattingPrompt = content.includes('Convert this veterinary content into') ||
                                    content.includes('Rewrite this veterinary content as') ||
                                    content.includes('Transform this veterinary content into');
            return !isFormattingPrompt;
          })
          .map(message => {
            // Use proper message_type field from backend instead of parsing content
            if (message.message_type === 'ai_generated_image' && message.image_url) {
              return {
                ...message,
                type: 'ai_generated_image'
              };
            }
            
            if (message.message_type === 'processed_image' && message.image_url) {
              return {
                ...message,
                type: 'processed_image'
              };
            }
            
            if (message.message_type === 'formatted_content' && message.platform) {
              return {
                ...message,
                type: 'formatted_content'
              };
            }
            
            // Legacy support: Check if this is an AI-generated image message (old format)
            if (message.content && message.content.includes('Type: ai_generated_image')) {
              // Extract image URL and S3 key from content
              const imageUrlMatch = message.content.match(/Image URL: (.*)/);
              const s3KeyMatch = message.content.match(/S3 Key: (.*)/);
              
              if (imageUrlMatch && s3KeyMatch) {
                return {
                  ...message,
                  image_url: imageUrlMatch[1].trim(),
                  s3_key: s3KeyMatch[1].trim(),
                  type: 'ai_generated_image',
                  content: message.content.split('\n')[0] // Use only the first line as display content
                };
              }
            }
            
            // Legacy support: Check if this is a processed image message (old format)
            if (message.content && message.content.includes('Type: processed_image')) {
              const imageUrlMatch = message.content.match(/Image URL: (.*)/);
              const s3KeyMatch = message.content.match(/S3 Key: (.*)/);
              const originalFileMatch = message.content.match(/Original File: (.*)/);
              
              if (imageUrlMatch && s3KeyMatch) {
                return {
                  ...message,
                  image_url: imageUrlMatch[1].trim(),
                  s3_key: s3KeyMatch[1].trim(),
                  type: 'processed_image',
                  original_file: originalFileMatch ? originalFileMatch[1].trim() : 'Unknown',
                  content: message.content.split('\n')[0] // Use only the first line as display content
                };
              }
            }
            
            return message;
          });
        
        setMessages(processedMessages);
        
        // Set platform/format from session data
        if (data.session) {
          setSelectedPlatform(data.session.selected_platform);
          setSelectedFormat(data.session.selected_format);
        }
      } else {
        throw new Error('Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const switchSession = async (session) => {
    setCurrentSession(session);
    await loadSessionMessages(session.id);
  };

  const openChatModal = async (session) => {
    setModalSession(session);
    setCurrentSession(session);
    await loadSessionMessages(session.id);
    setShowChatModal(true);
  };

  const closeChatModal = () => {
    setShowChatModal(false);
    setModalSession(null);
    setCurrentSession(null);
    setMessages([]);
    setInputMessage('');
    setTextareaRows(2); // Reset textarea height
  };

  const deleteSession = async (sessionId, e) => {
    if (e) e.stopPropagation();
    
    setShowDeletingModal(true); // Show deleting modal
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        
        // If we're deleting the current modal session, close the modal
        if (modalSession?.id === sessionId) {
          closeChatModal();
        }

        // Hide deleting modal after a brief delay
        setTimeout(() => {
          setShowDeletingModal(false);
        }, 500);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      setShowDeletingModal(false);
      toast({
        title: "Error",
        description: "Failed to delete session",
        variant: "destructive"
      });
    }
  };

  const sendMessage = async () => {
    if ((!inputMessage.trim() && uploadedFiles.length === 0) || !currentSession || isLoading) return;

    const userMessage = inputMessage.trim();
    const hasFiles = uploadedFiles.length > 0;
    
    setInputMessage('');
    setIsLoading(true);

    // Process uploaded files if they exist and user has given a command
    if (hasFiles && userMessage) {
      // Add user message with file info
      const userMsg = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: userMessage,
        created_at: new Date().toISOString(),
        files: uploadedFiles
      };
      setMessages(prev => [...prev, userMsg]);

      try {
        // Process each uploaded file with the user's command
        for (const file of uploadedFiles) {
          await processUploadedFile(file, userMessage);
        }
        
        // Clear uploaded files after processing
        setUploadedFiles([]);
        
      } catch (error) {
        console.error('Error processing files:', error);
        toast({
          title: "Error",
          description: "Failed to process uploaded files",
          variant: "destructive"
        });
      }
    } else if (userMessage) {
      // Regular text message without files
      // Add user message to UI immediately
      const userMsg = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: userMessage,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMsg]);

      try {
        // Send request with proper query parameter and body structure
        const response = await fetch(`${API_BASE_URL}/api/ai/chat?session_id=${encodeURIComponent(currentSession.id)}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: userMessage,
            model_override: selectedTextModel // Pass selected text model as override
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          // Remove temp user message and add both real messages
          setMessages(prev => {
            const filtered = prev.filter(m => m.id !== userMsg.id);
            return [
              ...filtered,
              {
                id: `user-${Date.now()}`,
                role: 'user',
                content: userMessage,
                created_at: new Date().toISOString()
              },
              data.message
            ];
          });

          // Update usage stats and session list
          if (data.remaining_credits !== undefined) {
            setAiSettings(prev => ({
              ...prev,
              credits_balance: data.remaining_credits
            }));
          }

          // Show low balance warning
          if (data.low_balance_warning) {
            toast({
              title: "Low Credit Balance",
              description: data.low_balance_warning,
              variant: "destructive"
            });
          }

          // Update session title if this was first message
          if (currentSession && messages.length === 0) {
            // Use user's first input as the chat name (truncated to 50 characters)
            const chatTitle = userMessage.length > 50 
              ? userMessage.substring(0, 50) + '...' 
              : userMessage;
              
            const updatedSessions = sessions.map(s => 
              s.id === currentSession.id 
                ? { ...s, title: chatTitle }
                : s
            );
            setSessions(updatedSessions);
            
            if (modalSession) {
              setModalSession(prev => ({
                ...prev,
                title: chatTitle
              }));
            }

            // Also update the session title on the backend
            try {
              await fetch(`${API_BASE_URL}/api/ai/sessions/${currentSession.id}/title`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  title: chatTitle
                })
              });
            } catch (titleError) {
              console.error('Error updating session title:', titleError);
              // Don't throw error, continue with chat functionality
            }
          }

          // Refresh usage stats
          await loadUsageStats();

        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to send message');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        
        // Remove temp user message on error
        setMessages(prev => prev.filter(m => m.id !== userMsg.id));
        
        toast({
          title: "Error",
          description: error.message || "Failed to send message",
          variant: "destructive"
        });
      }
    }
    
    setIsLoading(false);
  };

  // Helper function to get original prompt from message ID
  const getOriginalPrompt = (messageId, currentMessageId = null) => {
    // If we're regenerating an existing image, find the original prompt
    if (currentMessageId) {
      const currentMessage = messages.find(msg => msg.id === currentMessageId);
      if (currentMessage && currentMessage.original_message_id) {
        // This is already a generated image, get the original prompt from the referenced message
        const originalMessage = messages.find(msg => msg.id === currentMessage.original_message_id);
        return originalMessage ? originalMessage.content : null;
      }
    }
    
    // Otherwise, find the message by the provided messageId
    const message = messages.find(msg => msg.id === messageId);
    return message ? message.content : null;
  };

  // Generate AI Image using OpenRouter image generation
  const generateAIImage = async (content, messageId, platform = null, format = null, isRegeneration = false) => {
    setIsGeneratingImage(messageId);
    setShowGeneratingModal(true);
    
    // Dynamic modal messages to keep connection alive
    const modalMessages = [
      "Optimizing content & generating image...",
      "Still working on it...",
      "Creating your image...",
      "Almost there...",
      "Finalizing image generation..."
    ];
    let messageIndex = 0;
    setGeneratingMessage(modalMessages[0]);
    
    // Update modal message every 5 seconds to keep connection alive
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % modalMessages.length;
      setGeneratingMessage(modalMessages[messageIndex]);
    }, 5000);
    
    // Create AbortController with 120-second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    
    try {
      // Get the original prompt content
      let promptContent = content;
      
      // If this is a regeneration, get the original prompt from the referenced message
      if (isRegeneration) {
        const originalPrompt = getOriginalPrompt(messageId, messageId);
        if (originalPrompt) {
          promptContent = originalPrompt;
          console.log('Using original prompt for regeneration:', originalPrompt);
        } else {
          console.warn('Could not find original prompt for regeneration, using current content');
        }
      }
      
      // Pass raw content to backend for optimization - backend will clean up and create perfect image prompt
      const requestBody = {
        prompt: promptContent  // Send raw content, backend will optimize it
      };
      
      // Add selected image model if available
      if (selectedImageModel) {
        requestBody.model = selectedImageModel;
        console.log('🎨 Using selected image model:', selectedImageModel);
      } else {
        console.warn('⚠️ No image model selected, backend will use default');
      }

      // Add platform and format if specified
      if (platform && format) {
        requestBody.platform = platform;
        requestBody.format = format;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/ai/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      // Clear timeout if request completes successfully
      clearTimeout(timeoutId);
      clearInterval(messageInterval);
      
      if (response.ok) {
        const data = await response.json();
        
        // Create simple image message content without visible text
        const imageMessageContent = ""; // Empty content - only show the image
        
        // Determine the correct original message ID to store
        let originalMessageId = messageId;
        if (isRegeneration) {
          const currentMessage = messages.find(msg => msg.id === messageId);
          if (currentMessage && currentMessage.original_message_id) {
            // This is already a generated image, preserve the original message ID
            originalMessageId = currentMessage.original_message_id;
          }
        }
        
        // Save the image message to the database through the chat API with special flag to prevent AI response
        const saveResponse = await fetch(`${API_BASE_URL}/api/ai/chat?session_id=${encodeURIComponent(currentSession.id)}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: imageMessageContent,
            image_url: data.image_url,
            s3_key: data.s3_key,
            message_type: 'ai_generated_image',
            platform: data.platform,
            format: data.format,
            dimensions: data.dimensions,
            original_message_id: originalMessageId,  // Store reference to the message that contains the prompt
            skip_ai_response: true  // Prevent AI from generating followup response
          })
        });
        
        if (saveResponse.ok) {
          const savedData = await saveResponse.json();
          
          // Add the properly saved message to local state
          const imageMessage = {
            ...savedData.message,
            image_url: data.image_url,
            s3_key: data.s3_key,
            type: 'ai_generated_image',
            platform: data.platform,
            format: data.format,
            dimensions: data.dimensions
          };
          
          setMessages(prev => [...prev, imageMessage]);
        } else {
          // Fallback: add to local state only if database save fails
          const imageMessage = {
            id: `image-${Date.now()}`,
            role: 'assistant',
            content: imageMessageContent,
            created_at: new Date().toISOString(),
            image_url: data.image_url,
            s3_key: data.s3_key,
            originalMessageId: messageId,
            type: 'ai_generated_image',
            platform: data.platform,
            format: data.format,
            dimensions: data.dimensions
          };
          
          setMessages(prev => [...prev, imageMessage]);
        }
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate image');
      }
      
    } catch (error) {
      clearInterval(messageInterval);
      console.error('Error generating AI image:', error);
      
      // Handle timeout error specifically
      if (error.name === 'AbortError') {
        setErrorModal({
          show: true,
          message: "Image generation timed out after 120 seconds. Please try again with a shorter prompt or different settings."
        });
      } else {
        setErrorModal({
          show: true,
          message: error.message || "Failed to generate AI image"
        });
      }
    } finally {
      clearInterval(messageInterval);
      setIsGeneratingImage(null);
      setShowGeneratingModal(false);
    }
  };
  // Handle file upload
  const handleFileUpload = async (file) => {
    setIsUploadingFile(true);
    
    try {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only images (JPG, PNG, GIF, WebP) and PDF files are supported');
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }
      
      // Create form data for upload to Chatbot Uploads folder
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder_type', 'Chatbot Uploads');
      // No need for path since we're using the exact folder name
      
      const response = await fetch(`${API_BASE_URL}/api/s3/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const uploadedFile = {
          id: Date.now(),
          name: file.name,
          type: file.type,
          size: file.size,
          url: data.file_url,
          s3_key: data.s3_key
        };
        
        setUploadedFiles(prev => [...prev, uploadedFile]);
        
        toast({
          title: "File Uploaded",
          description: `${file.name} uploaded to Chatbot Uploads folder. Now tell me what you'd like me to do with it!`
        });
        
      } else {
        throw new Error('Failed to upload file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Upload Error",
        description: error.message || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Remove uploaded file
  const removeUploadedFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Process uploaded file with AI based on user command
  const processUploadedFile = async (file, userCommand) => {
    try {
      if (file.type.startsWith('image/')) {
        // Handle image processing using the existing generate-image endpoint
        // Create a detailed prompt that incorporates the user's request
        const imagePrompt = `Based on the uploaded image "${file.name}", ${userCommand}. Create a professional veterinary-themed image with the requested modifications. Make it suitable for professional use.`;
        
        const response = await fetch(`${API_BASE_URL}/api/ai/generate-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt: imagePrompt,
            size: "1024x1024", // Default size, user can specify different in command
            reference_image: file.url // Include reference to uploaded image
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Save the result as a message to the database
          const saveResponse = await fetch(`${API_BASE_URL}/api/ai/chat?session_id=${encodeURIComponent(currentSession.id)}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              content: `I've processed your image "${file.name}" based on your request: "${userCommand}". Here's the result:

Image URL: ${data.image_url}
S3 Key: ${data.s3_key}
Type: processed_image
Original File: ${file.name}`,
              message_type: 'processed_image',
              image_url: data.image_url,
              s3_key: data.s3_key,
              original_file: file.name
            })
          });
          
          if (saveResponse.ok) {
            const savedData = await saveResponse.json();
            
            // Add the saved message to local state with proper formatting
            const resultMessage = {
              ...savedData.message,
              image_url: data.image_url,
              s3_key: data.s3_key,
              type: 'processed_image',
              original_file: file.name
            };
            
            setMessages(prev => [...prev, resultMessage]);
          } else {
            // Fallback: add to local state only
            const resultMessage = {
              id: `processed-${Date.now()}`,
              role: 'assistant',
              content: `I've processed your image "${file.name}" based on your request: "${userCommand}".`,
              created_at: new Date().toISOString(),
              image_url: data.image_url,
              s3_key: data.s3_key,
              type: 'processed_image',
              original_file: file.name
            };
            
            setMessages(prev => [...prev, resultMessage]);
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to process image');
        }
        
      } else if (file.type === 'application/pdf') {
        // Handle PDF processing - for now, just provide a response about the PDF
        const pdfResponse = `I've received your PDF file "${file.name}". You requested: "${userCommand}". 

Currently, I can help you with image processing and generation. For PDF processing, I can:
- Describe what you'd like me to help you with regarding this PDF
- Generate new content based on your description
- Create visual materials related to the PDF content

Please let me know how else I can assist you with this document.`;
        
        // Save PDF processing response
        const saveResponse = await fetch(`${API_BASE_URL}/api/ai/chat?session_id=${encodeURIComponent(currentSession.id)}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: pdfResponse,
            message_type: 'processed_document',
            original_file: file.name
          })
        });
        
        if (saveResponse.ok) {
          const savedData = await saveResponse.json();
          
          const resultMessage = {
            ...savedData.message,
            type: 'processed_document',
            original_file: file.name
          };
          
          setMessages(prev => [...prev, resultMessage]);
        } else {
          // Fallback
          const resultMessage = {
            id: `processed-${Date.now()}`,
            role: 'assistant',
            content: pdfResponse,
            created_at: new Date().toISOString(),
            type: 'processed_document',
            original_file: file.name
          };
          
          setMessages(prev => [...prev, resultMessage]);
        }
      }
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: "Processing Error",
        description: error.message || "Failed to process file. Please try again.",
        variant: "destructive"
      });
    }
  };
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopyModal(true);
      
      // Hide modal after 1 second
      setTimeout(() => {
        setShowCopyModal(false);
      }, 1000);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy message",
        variant: "destructive"
      });
    }
  };

  // Handle image download
  const downloadImage = async (imageUrl, filename) => {
    try {
      // Use a more reliable download method for S3 images
      const link = document.createElement('a');
      
      // Set the href to the image URL
      link.href = imageUrl;
      
      // Set download attribute with filename
      link.download = filename || 'ai-generated-image.png';
      
      // Set additional attributes for better browser compatibility
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Add to document temporarily
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      
      // Removed toast notification for image download to reduce UI clutter
      
    } catch (error) {
      console.error('Error downloading image:', error);
      
      // Fallback: open image in new tab for manual save
      try {
        window.open(imageUrl, '_blank', 'noopener,noreferrer');
        toast({
          title: "Image Opened",
          description: "Image opened in new tab. Right-click and select 'Save image as...' to download."
        });
      } catch (fallbackError) {
        console.error('Error opening image:', fallbackError);
        toast({
          title: "Error",
          description: "Unable to download image. Please try copying the image URL.",
          variant: "destructive"
        });
      }
    }
  };

  // Format content for different platforms using AI generation
  const formatContent = async (content, platform, messageId) => {
    try {
      setIsLoading(true);
      
      // Auto-scroll to bottom to show the dotted animation with proper spacing
      scrollToBottom(true);
      
      let formattedContent = '';
      
      if (platform === 'email') {
        // Use the same logic as in AI agents for email
        const response = await fetch(`${API_BASE_URL}/api/ai/format-content`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: content,
            format_type: platform,
            agent_type: 'email_agent'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          formattedContent = data.formatted_content || data.content;
        } else {
          throw new Error(`Failed to format ${platform} content`);
        }
      } else {
        // For social media platforms, use the AI service generate_social_media_content
        const response = await fetch(`${API_BASE_URL}/api/ai/format-social-content`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: content,
            platform: platform,
            use_agent_format: true
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Extract only the content without extra explanations
          formattedContent = data.content || data.formatted_content;
          
          // Remove any AI explanation text patterns more thoroughly
          formattedContent = formattedContent
            .replace(/^.*?Here's.*?:.*?\n/gim, '')
            .replace(/^.*?looks great!.*?\n/gim, '')
            .replace(/^.*?This post is.*?\n/gim, '')
            .replace(/^.*?engaging and informative!.*?\n/gim, '')
            .replace(/---\s*\n/gim, '')
            .replace(/This version.*$/gim, '')
            .replace(/Feel free to adjust.*$/gim, '')
            .replace(/Let me know if.*$/gim, '')
            .replace(/Here are a few suggestions.*$/gim, '')
            .replace(/\d+\.\s+\*\*.*?\*\*:.*$/gim, '')  // Remove numbered suggestions
            .replace(/1\. \*\*Add a Statistic Source\*\*.*$/gim, '')
            .replace(/2\. \*\*Visual Suggestion\*\*.*$/gim, '')
            .replace(/3\. \*\*Encourage Sharing\*\*.*$/gim, '')
            .split('\n')
            .filter(line => !line.match(/^\d+\.\s+\*\*/))  // Remove lines starting with numbered suggestions
            .join('\n')
            .trim();
        } else {
          const errorText = await response.text();
          console.error(`Format API response error:`, errorText);
          throw new Error(`Failed to format ${platform} content: ${response.status} ${response.statusText}`);
        }
      }
      
      // Add formatted content as a new message (save this to the database)
      const saveResponse = await fetch(`${API_BASE_URL}/api/ai/chat?session_id=${encodeURIComponent(currentSession.id)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: formattedContent,
          message_type: 'formatted_content',
          platform: platform,
          original_message_id: messageId,
          skip_ai_response: true  // Prevent AI from generating followup response
        })
      });
      
      if (saveResponse.ok) {
        const savedData = await saveResponse.json();
        
        // Add the saved message to local state with platform info
        const newMessage = {
          ...savedData.message,
          platform: platform,
          original_message_id: messageId,
          id: savedData.message.id || `msg_${Date.now()}`,
          role: 'assistant',
          created_at: savedData.message.created_at || new Date().toISOString()
        };
        
        setMessages(prev => [...prev, newMessage]);
        
        setShowFormatMenu(null);
        
        // Removed toast notification for content formatting to reduce UI clutter
        
      } else {
        const saveErrorText = await saveResponse.text();
        console.error(`Chat save response error:`, saveErrorText);
        throw new Error(`Failed to save formatted content: ${saveResponse.status} ${saveResponse.statusText}`);
      }
    } catch (error) {
      console.error(`Error formatting content for ${platform}:`, error);
      console.error('Full error details:', {
        message: error.message,
        stack: error.stack,
        platform: platform,
        content: content
      });
      
      // Show error message in chat
      const errorMessage = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: `Sorry, I couldn't format the content for ${platform}. Please try again. Error: ${error.message}`,
        created_at: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle textarea auto-resize with fixed height management
  const handleTextareaChange = (e) => {
    setInputMessage(e.target.value);
    
    // Don't auto-resize - keep fixed height
    // This prevents the growing height bug
  };

  // Handle Escape key to close modal and format menu
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (showFormatMenu) {
          setShowFormatMenu(null);
        } else if (showImageGenMenu) {
          setShowImageGenMenu(null);
          setShowPlatformMenu(null);
          setShowFormatSubMenu(null);
          setSelectedPlatform(null);
        } else if (showChatModal) {
          closeChatModal();
        }
      }
    };

    const handleClickOutside = (e) => {
      if (showFormatMenu && !e.target.closest('.format-menu-container')) {
        setShowFormatMenu(null);
      }
      if (showImageGenMenu && !e.target.closest('.image-gen-menu-container')) {
        setShowImageGenMenu(null);
        setShowPlatformMenu(null);
        setShowFormatSubMenu(null);
        setSelectedPlatform(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showChatModal, showFormatMenu, showImageGenMenu]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showChatModal) {
      // Disable scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scrolling is re-enabled
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showChatModal]);

  // Credit purchases and settings now handled in AI Settings and AI Costs pages

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Bot className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Authentication Required</h3>
          <p className="mt-1 text-sm text-gray-500">Please log in to access the AI Chatbot.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #4B5563;
          border-radius: 1px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #6B7280;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #4B5563 transparent;
        }
      `}</style>
      
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            
        {/* Chat Sessions Grid */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Bot className="h-6 w-6 text-gray-700" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">AI Chatbot</h3>
                <p className="text-sm text-gray-600">Intelligent AI assistant for veterinary marketing and business operations</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {/* New Chat Button - Centered */}
            <div className="flex justify-center mb-6">
              <button
                onClick={createNewSession}
                className="text-white rounded-lg px-6 py-3 flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
                style={{ backgroundColor: 'var(--theme-primary)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-secondary)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
              >
                <Plus className="h-5 w-5 mr-2" />
                Start New Chat
              </button>
            </div>
            
            {/* Sessions List */}
            {sessions.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <MessageCircle className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No chat sessions yet</h3>
                <p className="mt-2 text-gray-600">Start your first conversation with the AI assistant!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => openChatModal(session)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {session.title}
                          </h4>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(session.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{new Date(session.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => deleteSession(session.id, e)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity ml-2 p-1"
                        title="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        </div>

      {/* Chat Modal */}
      {showChatModal && modalSession && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onWheel={(e) => {
            // Only prevent if the scroll target is not within the modal content
            if (e.target === e.currentTarget) {
              e.preventDefault();
            }
          }}
        >
          <div 
            className="bg-black rounded-xl shadow-2xl w-full max-w-5xl" 
            style={{ height: '96vh' }}
            onWheel={(e) => {
              // Stop propagation to prevent background scrolling
              e.stopPropagation();
            }}
          >
            {/* Modal Header - Compact */}
            <div 
              className="flex items-center justify-between px-4 py-2.5 border-b border-gray-700 rounded-t-xl bg-black"
            >
              <div className="flex items-center space-x-2.5">
                <Bot className="h-4 w-4 text-white" />
                <div>
                  <h3 className="text-sm font-semibold text-white">{modalSession.title}</h3>
                  <p className="text-xs text-gray-400">
                    {aiSettings?.openrouter_default_model && `Using ${aiSettings.openrouter_default_model}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={closeChatModal}
                  className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Chat Area */}
            <div className="flex flex-col bg-black" style={{ height: 'calc(96vh - 64px)' }}>
              {/* Messages - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-400 mt-20">
                    <Bot className="mx-auto h-12 w-12 text-gray-600 mb-4" />
                    <h3 className="text-lg font-medium text-white">Start a conversation</h3>
                    <p className="mt-2 text-gray-400">Ask me anything about veterinary services, pet care, business operations, or to write customer emails or social media posts!</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex max-w-4xl ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                        <div className={`flex-shrink-0 ${message.role === 'user' ? 'ml-3' : 'mr-3'}`}>
                          {message.role === 'user' ? (
                            <div className="h-8 w-8 rounded-full flex items-center justify-center bg-gray-600">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          ) : (
                            <div className="h-8 w-8 bg-gray-600 rounded-full flex items-center justify-center">
                              <Bot className="h-4 w-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div
                            className={`rounded-lg px-4 py-3 ${
                              message.role === 'user'
                                ? 'bg-gray-700 text-white border border-gray-600'
                                : 'bg-gray-800 text-white border border-gray-700'
                            }`}
                          >
                            <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                            
                            {/* Show platform indicator for formatted content */}
                            {message.type === 'formatted_content' && message.platform && (
                              <div className="text-xs text-blue-400 mt-2 font-medium">
                                ✨ Formatted for {
                                  message.platform === 'tiktok' ? 'TikTok' :
                                  message.platform === 'x' ? 'X (Twitter)' :
                                  message.platform === 'whatsapp' ? 'WhatsApp' :
                                  message.platform === 'instagram' ? 'Instagram' :
                                  message.platform === 'facebook' ? 'Facebook' :
                                  message.platform === 'email' ? 'Email' :
                                  message.platform.charAt(0).toUpperCase() + message.platform.slice(1)
                                }
                              </div>
                            )}
                            
                            {/* Timestamp */}
                            <div className="text-xs text-gray-400 mt-1">
                              {formatFriendlyTimestamp(message.created_at)}
                            </div>
                            
                            {/* Display uploaded files in user messages */}
                            {message.files && message.files.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {message.files.map(file => (
                                  <div key={file.id} className="flex items-center space-x-2 p-2 bg-gray-600 rounded border border-gray-500">
                                    {file.type.startsWith('image/') ? (
                                      <>
                                        <Image className="h-4 w-4 text-blue-300" />
                                        <img src={file.url} alt={file.name} className="w-16 h-16 object-cover rounded" />
                                      </>
                                    ) : (
                                      <FileText className="h-4 w-4 text-red-300" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-white truncate">{file.name}</p>
                                      <p className="text-xs text-gray-300">{(file.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Display AI generated image */}
                            {message.image_url && (message.type === 'ai_generated_image' || message.type === 'processed_image') && (
                              <div className="mt-3">
                                <img 
                                  src={message.image_url} 
                                  alt="AI Generated Image"
                                  className="max-w-full h-auto rounded-lg border border-gray-600"
                                  style={{ maxHeight: '300px' }}
                                />
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex-1">
                                    {/* Only show label for processed images, not AI-generated images */}
                                    {message.type === 'processed_image' && (
                                      <p className="text-xs text-gray-400">
                                        Processed image from: {message.original_file}
                                      </p>
                                    )}
                                    {/* Show platform, format and size for AI-generated images */}
                                    {message.type === 'ai_generated_image' && message.platform && message.format && (
                                      <div className="text-xs text-gray-400">
                                        <span className="font-medium">
                                          {
                                            message.platform === 'tiktok' ? 'TikTok' :
                                            message.platform === 'x' ? 'X (Twitter)' :
                                            message.platform === 'whatsapp' ? 'WhatsApp' :
                                            message.platform === 'instagram' ? 'Instagram' :
                                            message.platform === 'facebook' ? 'Facebook' :
                                            message.platform === 'marketing' ? 'Promotion Material' :
                                            message.platform.charAt(0).toUpperCase() + message.platform.slice(1)
                                          }
                                        </span>
                                        <span className="mx-1">•</span>
                                        <span>
                                          {
                                            message.format === 'story_reels' ? 'Story/Reels' :
                                            message.format === 'profile_picture' ? 'Profile Picture' :
                                            message.format === 'cover_photo' ? 'Cover Photo' :
                                            message.format === 'poster' ? 'Poster' :
                                            message.format === 'flyer' ? 'Flyer' :
                                            message.format.charAt(0).toUpperCase() + message.format.slice(1)
                                          }
                                        </span>
                                        {message.dimensions && (
                                          <>
                                            <span className="mx-1">•</span>
                                            <span>{message.dimensions}</span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => downloadImage(message.image_url, `${message.type}-${message.id}.png`)}
                                    className="flex items-center space-x-1 text-gray-400 hover:text-white text-xs transition-colors px-2 py-1 rounded hover:bg-gray-700 ml-2"
                                  >
                                    <Download className="h-3 w-3" />
                                    <span>Download</span>
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {/* Platform info now shown next to download button for AI-generated images */}
                            
                            {message.type === 'processed_document' && (
                              <div className="text-xs text-gray-400 mt-2 italic">
                                Processed document: {message.original_file}
                              </div>
                            )}
                          </div>
                          
                          {/* Copy and Format buttons for AI messages */}
                          {message.role === 'assistant' && (
                            <div className="flex items-center mt-2 space-x-4">
                              <button
                                onClick={() => copyToClipboard(message.content)}
                                className="flex items-center space-x-1 text-gray-400 hover:text-white text-xs transition-colors"
                              >
                                <Copy className="h-3 w-3" />
                                <span>Copy</span>
                              </button>
                              
                              {/* Only show format options for non-image messages */}
                              {message.message_type !== 'ai_generated_image' && !message.image_url && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500">Format:</span>
                                  <div className="relative format-menu-container">
                                  <button
                                    onClick={() => setShowFormatMenu(showFormatMenu === message.id ? null : message.id)}
                                    className="flex items-center space-x-1 text-gray-400 hover:text-white text-xs transition-colors"
                                  >
                                    <MoreVertical className="h-3 w-3" />
                                  </button>
                                  
                                  {showFormatMenu === message.id && (
                                    <div className="absolute left-0 bottom-6 bg-gray-800 border border-gray-600 rounded-lg shadow-lg py-1 w-32 z-10">
                                      <button
                                        onClick={() => {
                                          formatContent(message.content, 'email', message.id);
                                          setShowFormatMenu(null); // Close menu after click
                                        }}
                                        className="w-full text-left px-3 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                      >
                                        <Mail className="h-3 w-3" />
                                        <span>Email</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          formatContent(message.content, 'tiktok', message.id);
                                          setShowFormatMenu(null); // Close menu after click
                                        }}
                                        className="w-full text-left px-3 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                      >
                                        <Share2 className="h-3 w-3" />
                                        <span>TikTok</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          formatContent(message.content, 'facebook', message.id);
                                          setShowFormatMenu(null); // Close menu after click
                                        }}
                                        className="w-full text-left px-3 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                      >
                                        <Share2 className="h-3 w-3" />
                                        <span>Facebook</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          formatContent(message.content, 'instagram', message.id);
                                          setShowFormatMenu(null); // Close menu after click
                                        }}
                                        className="w-full text-left px-3 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                      >
                                        <Share2 className="h-3 w-3" />
                                        <span>Instagram</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          formatContent(message.content, 'whatsapp', message.id);
                                          setShowFormatMenu(null); // Close menu after click
                                        }}
                                        className="w-full text-left px-3 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                      >
                                        <MessageSquare className="h-3 w-3" />
                                        <span>WhatsApp</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          formatContent(message.content, 'x', message.id);
                                          setShowFormatMenu(null); // Close menu after click
                                        }}
                                        className="w-full text-left px-3 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                      >
                                        <Share2 className="h-3 w-3" />
                                        <span>X</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              )}
                              
                              {/* AI Image Generation Dropdown */}
                              <div className="relative image-gen-menu-container">
                                <button
                                  onClick={() => {
                                    if (isGeneratingImage === message.id) return;
                                    setShowImageGenMenu(showImageGenMenu === message.id ? null : message.id);
                                    setShowPlatformMenu(null);
                                    setShowFormatSubMenu(null);
                                    setSelectedPlatform(null);
                                  }}
                                  disabled={isGeneratingImage === message.id}
                                  className="flex items-center space-x-1 text-gray-400 hover:text-white text-xs transition-colors disabled:opacity-50"
                                >
                                  <Image className="h-3 w-3" />
                                  <span>
                                    {message.message_type === 'ai_generated_image' || message.image_url ? 
                                      'Re-Generate AI Image' : 
                                      'Generate AI Image'
                                    }
                                  </span>
                                  <MoreVertical className="h-3 w-3" />
                                </button>
                                
                                {/* Main Dropdown Menu */}
                                {showImageGenMenu === message.id && (
                                  <div className="absolute left-0 bottom-6 bg-gray-800 border border-gray-600 rounded-lg shadow-lg py-1 w-48 z-20">
                                    {/* Quick Generate Option */}
                                    <button
                                      onClick={() => {
                                        const isRegeneration = message.message_type === 'ai_generated_image' || message.image_url;
                                        generateAIImage(message.content, message.id, null, null, isRegeneration);
                                        setShowImageGenMenu(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                                    >
                                      <Image className="h-3 w-3" />
                                      <span>Quick Generate (Default)</span>
                                    </button>
                                    
                                    <div className="border-t border-gray-600 my-1"></div>
                                    
                                    {/* Platform Options */}
                                    {Object.keys(socialMediaPlatforms).map((platformKey) => {
                                      const platform = socialMediaPlatforms[platformKey];
                                      return (
                                        <div key={platformKey} className="relative">
                                          <button
                                            onClick={() => {
                                              if (selectedPlatform === platformKey) {
                                                setSelectedPlatform(null);
                                                setShowFormatSubMenu(null);
                                              } else {
                                                setSelectedPlatform(platformKey);
                                                setShowFormatSubMenu(platformKey);
                                              }
                                            }}
                                            className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center justify-between"
                                          >
                                            <div className="flex items-center space-x-2">
                                              <span>{platform.icon}</span>
                                              <span>{platform.name}</span>
                                            </div>
                                            <span className="text-gray-500">›</span>
                                          </button>
                                          
                                          {/* Format Submenu */}
                                          {showFormatSubMenu === platformKey && (
                                            <div className="absolute left-full top-0 bg-gray-800 border border-gray-600 rounded-lg shadow-lg py-1 w-40 ml-1 z-30">
                                              {Object.keys(platform.formats).map((formatKey) => (
                                                <button
                                                  key={formatKey}
                                                  onClick={() => {
                                                    const isRegeneration = message.message_type === 'ai_generated_image' || message.image_url;
                                                    generateAIImage(message.content, message.id, platformKey, formatKey, isRegeneration);
                                                    setShowImageGenMenu(null);
                                                    setShowFormatSubMenu(null);
                                                    setSelectedPlatform(null);
                                                  }}
                                                  className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white"
                                                >
                                                  {platform.formats[formatKey]}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex mr-3">
                      <div className="h-8 w-8 bg-gray-600 rounded-full flex items-center justify-center">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-lg px-4 py-3 border border-gray-700">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Modal Input Area - Compact Design */}
              <div className="border-t border-gray-700 px-3 py-2.5 bg-gray-900">
                {/* Uploaded Files Preview */}
                {uploadedFiles.length > 0 && (
                  <div className="mb-2 p-2 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-400">Uploaded files:</span>
                      <span className="text-xs text-gray-500">Tell me what to do with them!</span>
                    </div>
                    <div className="space-y-1.5">
                      {uploadedFiles.map(file => (
                        <div key={file.id} className="flex items-center space-x-2 p-1.5 bg-gray-700 rounded border border-gray-600">
                          {file.type.startsWith('image/') ? (
                            <>
                              <Image className="h-3.5 w-3.5 text-blue-300 flex-shrink-0" />
                              <img src={file.url} alt={file.name} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                            </>
                          ) : (
                            <FileText className="h-3.5 w-3.5 text-red-300 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-white truncate">{file.name}</p>
                            <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <button
                            onClick={() => removeUploadedFile(file.id)}
                            className="text-gray-400 hover:text-red-400 p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Chat Input Container - Compact */}
                <div className="relative bg-gray-800 rounded-lg border border-gray-600">
                  {/* Main Input Area */}
                  <div className="relative">
                    <textarea
                      value={inputMessage}
                      onChange={handleTextareaChange}
                      onKeyPress={handleKeyPress}
                      placeholder={uploadedFiles.length > 0 ? "Tell me what to do with the uploaded files..." : "Agent is waiting..."}
                      className="w-full bg-transparent text-sm text-white placeholder-gray-400 px-3 py-2 pr-12 resize-none overflow-y-auto focus:outline-none"
                      style={{
                        minHeight: '42px',
                        maxHeight: '120px'
                      }}
                      disabled={isLoading}
                    />
                    
                    {/* Send Button - Compact */}
                    <button
                      onClick={sendMessage}
                      disabled={((!inputMessage.trim() && uploadedFiles.length === 0) || isLoading)}
                      className="absolute top-1 right-1 w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-theme-blue text-white hover:bg-blue-700"
                    >
                      {isLoading ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                      )}
                    </button>
                  </div>
                  
                  {/* Action Bar - Compact */}
                  <div className="px-2.5 pb-2 pt-1.5 border-t border-gray-700 flex items-center justify-between gap-2">
                    {/* Left side - File Upload */}
                    <div className="flex items-center">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleFileUpload(file);
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                        id="file-upload"
                        disabled={isUploadingFile}
                      />
                      <label
                        htmlFor="file-upload"
                        className={`flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-colors ${
                          isUploadingFile 
                            ? 'bg-gray-700 cursor-not-allowed' 
                            : 'bg-gray-700 hover:bg-gray-600'
                        }`}
                        title="Attach file"
                      >
                        {isUploadingFile ? (
                          <RefreshCw className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                        ) : (
                          <Paperclip className="h-3.5 w-3.5 text-gray-300" />
                        )}
                      </label>
                    </div>
                    
                    {/* Right side - Model Selection Dropdowns - Very Compact */}
                    <div className="flex items-center gap-1.5 text-xs flex-1 justify-end">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 text-[10px]">Text:</span>
                        <select
                          value={selectedTextModel || ''}
                          onChange={(e) => setSelectedTextModel(e.target.value)}
                          className="px-1.5 py-0.5 text-[10px] bg-gray-700 text-gray-300 border border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select...</option>
                          {textModels.map((model) => (
                            <option key={model.value} value={model.value}>
                              {model.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 text-[10px]">Image:</span>
                        <select
                          value={selectedImageModel || ''}
                          onChange={(e) => setSelectedImageModel(e.target.value)}
                          className="px-1.5 py-0.5 text-[10px] bg-gray-700 text-gray-300 border border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select...</option>
                          {imageModels.map((model) => (
                            <option key={model.value} value={model.value}>
                              {model.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Copy Success Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-black bg-opacity-90 text-white px-6 py-4 rounded-lg shadow-lg border border-gray-600 pointer-events-auto">
            <div className="flex items-center space-x-3">
              <Check className="h-5 w-5 text-green-400" />
              <span className="text-sm font-medium">Message copied to clipboard</span>
            </div>
          </div>
        </div>
      )}

      {/* Generating Modal - Compact Dark Theme with Dynamic Message */}
      {showGeneratingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-4 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span className="text-sm text-white">{generatingMessage}</span>
            </div>
          </div>
        </div>
      )}

      {/* Deleting Modal */}
      {showDeletingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-4 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span className="text-sm text-white">Deleting...</span>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 shadow-xl max-w-md mx-4">
            <div className="text-center">
              <div className="text-red-400 text-2xl mb-3">⚠️</div>
              <h3 className="text-lg font-semibold text-white mb-3">Image Generation Error</h3>
              <p className="text-sm text-gray-300 mb-4">{errorModal.message}</p>
              <button
                onClick={() => setErrorModal({ show: false, message: '' })}
                className="bg-theme-blue hover:bg-blue-700 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      </div>
    </>
  );
};

export default AIChatbot;